import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-[#050810]" data-testid="terms-of-service-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <Link to="/home">
            <Button variant="ghost" className="text-[#8A92A0] hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16 px-6">
        <article className="max-w-3xl mx-auto">
          <header className="mb-12">
            <h1 className="font-['Unbounded'] text-4xl font-bold text-white mb-4">
              Terms of Service
            </h1>
            <p className="text-[#8A92A0]">Last updated: January 2026</p>
          </header>

          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                By accessing or using StardomFans Ltd. (&ldquo;the Platform&rdquo;), you agree to be bound by these Terms of Service.
                If you do not agree to these terms, please do not use the Platform. These terms apply to all users, 
                including artists, fans, and visitors.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">2. User Accounts</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                To access certain features, you must create an account. You agree to:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-2">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized access</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">3. Artist Accounts</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                Artists using the Platform agree to:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-2">
                <li>Only upload content they have the legal right to distribute</li>
                <li>Provide accurate event and product information</li>
                <li>Honor all ticket and merchandise sales</li>
                <li>Comply with all applicable laws regarding content and commerce</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">4. Payments, Fees, Refunds &amp; Merchant of Record</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-3">
                <strong className="text-white">4.1 Fees.</strong> StardomFans Ltd. charges a <strong className="text-white">12% platform commission</strong> on ticket sales, product sales, and digital-goods sales (<strong className="text-white">10% for Founders Circle artists</strong>). Wallet top-ups are <strong className="text-white">fee-free</strong> for buyers (1 Token = $1 USD, 1:1). Stripe payment-processing fees (2.9% + $0.30 charge fee plus 0.25% + $0.25 payout fee) are <strong className="text-white">netted from the artist's share at wire time</strong>, not added at top-up — the platform commission stays whole. Rates are shown to artists in the Dashboard fee-transparency card.
              </p>
              <p className="text-[#8A92A0] leading-relaxed mb-3">
                <strong className="text-white">4.2 Refund Policy — 7-Day Window.</strong> All sales of <strong className="text-white">digital products and services</strong> (digital downloads, albums, livestream passes, remix-challenge entries, AI messages, subscriptions) are <strong className="text-white">final and non-refundable</strong>. Exceptions are made only if the digital file is confirmed defective, misrepresented, or the initial download fails to complete. Refund requests for eligible digital items must be submitted <strong className="text-white">within 7 days after the date of purchase</strong> by contacting admin support in-app. For <strong className="text-white">physical goods and event tickets</strong>, refund requests are also accepted within the 7-day window; after day 7 all sales become final.
              </p>
              <p className="text-[#8A92A0] leading-relaxed mb-3">
                <strong className="text-white">4.3 Treasury Rule (7 / 10 / 11).</strong> To protect both buyers and sellers, funds move through a mandatory holdback schedule:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-1 mb-3">
                <li><strong className="text-white">Day 1–7:</strong> Refund window open. Funds are marked &ldquo;Pending&rdquo; — not available for artist payout.</li>
                <li><strong className="text-white">Day 8–10:</strong> Refund window closed. Funds remain &ldquo;Pending&rdquo; — this is the 3-day treasury grace period.</li>
                <li><strong className="text-white">Day 11+:</strong> Funds mature and move to the artist&rsquo;s &ldquo;Available&rdquo; balance for withdrawal.</li>
              </ul>
              <p className="text-[#8A92A0] leading-relaxed mb-3">
                <strong className="text-white">4.4 Merchant of Record (MoR).</strong> StardomFans Ltd. acts as the <strong className="text-white">Merchant of Record</strong> for all transactions processed on the Platform. This means we are legally responsible for collecting, remitting, and reporting applicable taxes (GST/HST/PST/QST) on behalf of Canadian sellers, as well as for handling chargebacks and payment-provider disputes. Buyers see &ldquo;StardomFans&rdquo; on their card statement. Artists receive net proceeds after platform commission, processing fees, and applicable taxes have been deducted at source.
              </p>
              <p className="text-[#8A92A0] leading-relaxed mb-3">
                <strong className="text-white">4.5 Chargebacks.</strong> A chargeback initiated by a buyer with their card issuer immediately reverses the full transaction amount, plus a $15 chargeback administration fee, from the artist&apos;s pending balance. Repeated chargebacks may result in account suspension pending investigation.
              </p>
              <p className="text-[#8A92A0] leading-relaxed">
                <strong className="text-white">4.6 Payout Schedule.</strong> Artists may withdraw matured &ldquo;Available&rdquo; balance at any time via the Payouts dashboard, subject to a minimum payout threshold and the two-person co-sign security policy for larger amounts. Payouts settle in 1–3 business days depending on the destination bank.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">5. Content Guidelines</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                Users may not upload or share content that:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-2">
                <li>Infringes on intellectual property rights</li>
                <li>Contains illegal, harmful, or offensive material</li>
                <li>Violates privacy or publicity rights</li>
                <li>Contains malware or malicious code</li>
                <li>Promotes fraud or deceptive practices</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">6. Intellectual Property</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                Artists retain ownership of their content. By uploading content to StardomFans Ltd., you grant us 
                a non-exclusive, worldwide license to host, display, and distribute your content on the 
                Platform. This license terminates when you remove your content or close your account.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">7. Content Disclaimer & Limitation of Liability</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                <strong className="text-white">7.1 Content Disclaimer.</strong> StardomFans Ltd. operates solely as a technology platform that enables 
                artists, corporate accounts, store operators, and individual users (collectively, &ldquo;Uploaders&rdquo;) to publish, 
                distribute, and monetize their creative works, products, and services. <strong className="text-white">StardomFans Ltd. does not create, 
                endorse, screen, verify, approve, or assume any legal responsibility or liability whatsoever for any content, 
                product, listing, message, or material uploaded, published, displayed, sold, streamed, or otherwise made 
                available by any Uploader or any third party on this Platform.</strong> This disclaimer applies without 
                limitation to all music, videos, images, livestreams, event listings, merchandise, descriptions, promotional 
                materials, comments, community posts, corporate store inventory, user-uploaded media, and any other 
                user-generated content of any kind.
              </p>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                <strong className="text-white">7.2 Uploader Responsibility.</strong> Each Uploader — whether an artist, a corporate account, a store 
                operator, or a regular user — is <strong className="text-white">solely and exclusively responsible</strong> for: 
                (a) all content they publish, post, stream, or distribute on StardomFans Ltd.; (b) all products, tickets, 
                merchandise, subscriptions, and services they list or sell through the Platform; (c) all dealings, 
                communications, and transactions with their fans, customers, and other users; (d) ensuring they hold all 
                necessary rights, licenses, permissions, and consents for any content or material they upload; and 
                (e) full compliance with all applicable local, national, and international laws, including but not limited 
                to copyright, trademark, publicity rights, consumer protection, defamation, privacy, data protection, tax, 
                obscenity, anti-discrimination, and import/export laws. Each Uploader acknowledges and agrees that 
                <strong className="text-white"> StardomFans Ltd. shall not be held liable</strong> for any disputes, claims, regulatory 
                actions, fines, damages, refunds, chargebacks, or losses arising from their content, products, services, 
                or customer interactions.
              </p>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                <strong className="text-white">7.3 Transaction Disclaimer.</strong> All purchases of tickets, merchandise, subscriptions, digital 
                downloads, livestream access, supporter experiences, and other goods or services listed on StardomFans Ltd. 
                constitute a <strong className="text-white">direct transaction between the purchasing user and the respective Uploader</strong> 
                (artist, corporate seller, or store operator). StardomFans Ltd. acts solely as a payment facilitator and 
                technology intermediary and is <strong className="text-white">not a party</strong> to these underlying transactions. StardomFans Ltd. bears 
                no responsibility for the quality, condition, authenticity, delivery, performance, refund, cancellation, 
                or fulfillment of any goods or services sold by Uploaders on the Platform. Refund and dispute resolution 
                policies are set and enforced by each Uploader and must comply with applicable consumer-protection laws.
              </p>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                <strong className="text-white">7.4 Moderation Reservation.</strong> While StardomFans Ltd. is not legally responsible for user-generated 
                content, we reserve the unconditional right (but not the obligation) to review, remove, restrict access to, 
                de-monetize, or take down any content that we determine, in our sole discretion, violates these Terms, 
                our Community Guidelines, our <Link to="/copyright" className="text-[#EC008C] hover:underline">Copyright Policy</Link>, 
                or any applicable law. Removal of content does not constitute legal endorsement of any remaining content.
              </p>
              <p className="text-[#8A92A0] leading-relaxed">
                <strong className="text-white">7.5 Limitation of Liability.</strong> StardomFans Ltd. and its parent companies, affiliates, officers, 
                directors, employees, agents, and partners shall <strong className="text-white">not be liable</strong> for any indirect, incidental, 
                special, consequential, exemplary, or punitive damages, including but not limited to loss of profits, revenue, 
                data, goodwill, or business opportunities, arising out of or in connection with your access to or use of 
                the Platform, your dealings with other users, or any content available on the Platform. The Platform is 
                provided on an <strong className="text-white">&ldquo;as is&rdquo; and &ldquo;as available&rdquo;</strong> basis without warranties of any kind, 
                whether express, implied, or statutory. To the maximum extent permitted by law, StardomFans Ltd.&apos;s total 
                aggregate liability arising out of or related to these Terms or your use of the Platform shall not exceed 
                the platform fees actually paid by you to StardomFans Ltd. in the twelve (12) months preceding the claim, 
                or one hundred U.S. dollars (USD $100), whichever is greater.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">8. Termination</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                We reserve the right to suspend or terminate accounts that violate these terms. You may 
                close your account at any time. Upon termination, your right to use the Platform ceases 
                immediately, though certain provisions of these terms will survive.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">9. Governing Law &amp; Jurisdiction</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                These terms are governed by and construed in accordance with the laws of the Province of{" "}
                <strong className="text-white">British Columbia</strong> and the federal laws of{" "}
                <strong className="text-white">Canada</strong> applicable therein, without regard to conflict-of-law
                principles. The parties irrevocably attorn to the exclusive jurisdiction of the courts sitting in
                Vancouver, British Columbia for any dispute not otherwise resolved by binding arbitration.
              </p>
              <p className="text-[#8A92A0] leading-relaxed mt-3">
                Any dispute, controversy, or claim arising out of or relating to these terms, or the breach,
                termination, or invalidity thereof, shall be finally resolved by arbitration administered by the{" "}
                <strong className="text-white">British Columbia International Commercial Arbitration Centre (BCICAC)</strong>{" "}
                pursuant to its Rules for Domestic Commercial Arbitration Proceedings, with the seat of arbitration
                in Vancouver, British Columbia, and the language of arbitration in English.
              </p>
              <p className="text-[#8A92A0] leading-relaxed mt-3">
                Consumer purchases originating in Canada are additionally protected by the{" "}
                <strong className="text-white">Business Practices and Consumer Protection Act (BC)</strong>,{" "}
                the <strong className="text-white">Personal Information Protection Act (BC)</strong>,{" "}
                and Canada&apos;s <strong className="text-white">Consumer Packaging and Labelling Act</strong>{" "}
                where applicable.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">10. Contact</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                For questions about these Terms of Service, please contact us via secure in-app messaging:{" "}
                <Link to="/message-admin" className="text-[#EC008C] hover:underline" data-testid="tos-contact-admin">
                  Message Admin (Legal)
                </Link>
              </p>
            </section>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{mixBlendMode:"lighten"}} />
          <div className="flex gap-6 flex-wrap justify-center">
            <Link to="/terms" className="text-[#EC008C] text-sm">Terms</Link>
            <Link to="/privacy" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Privacy</Link>
            <Link to="/copyright" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Copyright</Link>
            <Link to="/security" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Security</Link>
            <Link to="/privacy-report" className="text-[#00E68A] text-sm font-semibold">Privacy Report</Link>
          </div>
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
